import axios from 'axios'

export interface OfferImage {
  image_id: string
  image_url?: string
  is_primary: boolean
  display_order: number
  // Additional fields for blob images
  data_url?: string
  image_data?: string
  file_size?: number
  mime_type?: string
  compressed?: boolean
  compression_ratio?: number
}

export interface Offer {
  offer_id: string
  merchant_id: string
  type: 'discount' | 'bundle' | 'flash_sale' | 'seasonal'
  title: string
  description: string
  original_price: number | null
  discount_price: number | null
  discount_percentage: number | null
  category_id: string | null
  start_date: string | null
  end_date: string | null
  max_quantity: number | null
  currency_code?: string | null
  preparation_time_minutes?: number | null
  is_active: boolean
  created_at: string
  updated_at: string
  merchant_name?: string
  merchant_logo?: string
  category_name?: string
  product_count?: number
  products?: OfferProduct[]
  images?: OfferImage[]
}

export interface OfferProduct {
  product_id: string
  name: string
  price: number
  quantity: number
}

export interface CreateOfferDto {
  merchant_id: string
  type: 'discount' | 'bundle' | 'flash_sale' | 'seasonal' | 'surprise_bag'
  title: string
  description?: string
  original_price?: number | null
  discount_price?: number | null
  discount_percentage?: number | null
  category_id?: string | null
  start_date?: string | null
  end_date?: string | null
  max_quantity?: number | null
  currency?: string | null
  preparation_time_minutes?: number | null
  is_surprise_bag?: boolean
  surprise_bag_price?: number | null
  surprise_bag_label?: string
  possible_contents?: string[]
  products?: Array<{
    product_id: string
    quantity?: number
  }>
  images?: string[] // For backward compatibility, not used for blob uploads
}

export interface UpdateOfferDto extends Partial<CreateOfferDto> {
  is_active?: boolean
  delete_images?: string[] // Array of image_ids to delete
}

export interface OfferFilters {
  merchant_id?: string
  type?: string
  is_active?: boolean
  category_id?: string
  page?: number
  limit?: number
  search?: string
}

export interface OfferStats {
  total: number
  active: number
  expired: number
  by_type: Record<string, number>
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const offerApi = axios.create({
  baseURL: `${API_BASE_URL}/offers`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
offerApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
offerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const offerService = {
  // Get all offers with optional filters
  async getOffers(filters: OfferFilters = {}) {
    const params = new URLSearchParams()
    
    if (filters.merchant_id) params.append('merchant_id', filters.merchant_id)
    if (filters.type) params.append('type', filters.type)
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters.category_id) params.append('category_id', filters.category_id)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.search) params.append('search', filters.search)
    
    const response = await offerApi.get('', { params })
    return response.data
  },

  // Get offer by ID
  async getOfferById(id: string) {
    const response = await offerApi.get(`/${id}`)
    return response.data
  },

  // Create new offer
  async createOffer(data: CreateOfferDto | FormData) {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;
    
    if (isFormData) {
      // Send FormData directly as multipart/form-data
      const response = await axios.post(`${API_BASE_URL}/offers`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
        }
      });
      return response.data;
    } else {
      // Check if there are images that need to be sent as FormData
      const hasImages = data.images && data.images.length > 0;
      
      if (hasImages) {
        // Convert base64 strings to files and send as FormData
        const formData = new FormData();
        
        // Add all non-image fields
        Object.keys(data).forEach(key => {
          if (key !== 'images') {
            const value = data[key as keyof CreateOfferDto];
            if (value !== undefined && value !== null) {
              if (key === 'products' && Array.isArray(value)) {
                // For products array, send as JSON
                formData.append(key, JSON.stringify(value));
              } else if (Array.isArray(value)) {
                // For other arrays, join them or send as JSON
                formData.append(key, JSON.stringify(value));
              } else {
                formData.append(key, value.toString());
              }
            }
          }
        });
        
        // Add images as files
        data.images?.forEach((image, index) => {
          // Check if image is a base64 string
          if (image.startsWith('data:image')) {
            // Convert base64 to blob
            const base64Data = image.split(',')[1];
            const mimeType = image.match(/data:(.*);base64/)?.[1] || 'image/jpeg';
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            
            // Create a file from blob
            const fileName = `image_${index}.${mimeType.split('/')[1] || 'jpg'}`;
            const file = new File([blob], fileName, { type: mimeType });
            formData.append('images', file);
          }
        });
        
        // Send as multipart/form-data
        const response = await axios.post(`${API_BASE_URL}/offers`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
          }
        });
        return response.data;
      } else {
        // Send as JSON
        const response = await offerApi.post('', data);
        return response.data;
      }
    }
  },

  // Update offer
  async updateOffer(id: string, data: UpdateOfferDto | FormData) {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;
    
    if (isFormData) {
      // Send FormData directly as multipart/form-data
      const response = await axios.put(`${API_BASE_URL}/offers/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
        }
      });
      return response.data;
    } else {
      // Check if there are images that need to be sent as FormData
      const hasImages = data.images && data.images.length > 0;
      const hasDeleteImages = data.delete_images && data.delete_images.length > 0;
      
      if (hasImages || hasDeleteImages) {
        // Convert base64 strings to files and send as FormData
        const formData = new FormData();
        
        // Add all non-image fields
        Object.keys(data).forEach(key => {
          if (key !== 'images' && key !== 'delete_images') {
            const value = data[key as keyof UpdateOfferDto];
            if (value !== undefined && value !== null) {
              if (key === 'products' && Array.isArray(value)) {
                // For products array, send as JSON
                formData.append(key, JSON.stringify(value));
              } else if (Array.isArray(value)) {
                // For other arrays, join them or send as JSON
                formData.append(key, JSON.stringify(value));
              } else {
                formData.append(key, value.toString());
              }
            }
          }
        });
        
        // Add delete_images if present
        if (data.delete_images && data.delete_images.length > 0) {
          formData.append('delete_images', JSON.stringify(data.delete_images));
        }
        
        // Add images as files
        data.images?.forEach((image, index) => {
          // Check if image is a base64 string
          if (image.startsWith('data:image')) {
            // Convert base64 to blob
            const base64Data = image.split(',')[1];
            const mimeType = image.match(/data:(.*);base64/)?.[1] || 'image/jpeg';
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            
            // Create a file from blob
            const fileName = `image_${index}.${mimeType.split('/')[1] || 'jpg'}`;
            const file = new File([blob], fileName, { type: mimeType });
            formData.append('images', file);
          }
        });
        
        // Send as multipart/form-data
        const response = await axios.put(`${API_BASE_URL}/offers/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
          }
        });
        return response.data;
      } else {
        // Send as JSON
        const response = await offerApi.put(`/${id}`, data);
        return response.data;
      }
    }
  },

  // Delete offer
  async deleteOffer(id: string) {
    const response = await offerApi.delete(`/${id}`)
    return response.data
  },

  // Update offer status
  async updateOfferStatus(id: string, is_active: boolean) {
    const response = await offerApi.patch(`/${id}/status`, { is_active })
    return response.data
  },

  // Get offers by merchant
  async getOffersByMerchant(merchantId: string) {
    const response = await offerApi.get(`/merchant/${merchantId}`)
    return response.data
  },

  // Get offer statistics
  async getOfferStats(): Promise<OfferStats> {
    const response = await offerApi.get('')
    const offers = response.data.data || []
    
    const now = new Date()
    const stats: OfferStats = {
      total: offers.length,
      active: 0,
      expired: 0,
      by_type: {}
    }
    
    offers.forEach((offer: Offer) => {
      // Count by type
      stats.by_type[offer.type] = (stats.by_type[offer.type] || 0) + 1
      
      // Check if active
      if (offer.is_active) {
        stats.active++
      }
      
      // Check if expired
      if (offer.end_date && new Date(offer.end_date) < now) {
        stats.expired++
      }
    })
    
    return stats
  },

  // Get offer types
  getOfferTypes() {
    return [
      { value: 'discount', label: 'Réduction', color: 'bg-green-100 text-green-800' },
      { value: 'bundle', label: 'Pack', color: 'bg-blue-100 text-blue-800' },
      { value: 'flash_sale', label: 'Vente flash', color: 'bg-red-100 text-red-800' },
      { value: 'seasonal', label: 'Saisonnière', color: 'bg-purple-100 text-purple-800' },
      { value: 'surprise_bag', label: 'Sac surprise', color: 'bg-amber-100 text-amber-800' }
    ]
  },

  // Format date for display
  formatDate(dateString: string | null) {
    if (!dateString) return 'Non définie'
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  },

  // Calculate discount percentage
  calculateDiscountPercentage(original: any, discount: any) {
    if (!original || !discount) return 0
    const originalNum = typeof original === 'string' ? parseFloat(original) : Number(original)
    const discountNum = typeof discount === 'string' ? parseFloat(discount) : Number(discount)
    if (isNaN(originalNum) || isNaN(discountNum) || originalNum <= 0) return 0
    return Math.round(((originalNum - discountNum) / originalNum) * 100)
  }
}
